import { Grow } from '@mui/material';
import Grid from '@mui/material/Grid';
import { differenceWith, isEqual } from 'lodash';
import { useState } from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import BlogCard from '@/components/BlogComponents/shared/BlogCard/BlogCard';
import Heading from '@/components/Heading/Heading';
import { IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { BlogPageType } from '@/contentful/api/api.types';
import { DYNAMIC_GRID_ARTICLE_LIMIT } from '@/contentful/constants';
import { toCamelCase } from '@/utils/utils';
import useStyles from './BlogArticle3Up.styles';

const BASE_DELAY = 300;
const DELAY_MULTIPLIER = 200;

const dedupe = (loadedData: BlogPageType[], initialItems: BlogPageType[]) => {
  if (loadedData.length === 0) return [];
  return differenceWith(loadedData, initialItems, isEqual);
};

function getAdditionalArticles(
  blogCategory: string | undefined,
  initialArticles: BlogPageType[],
  isCurated: boolean
): BlogPageType[] {
  let loadedData: BlogPageType[] = [];
  let additionalArticles: BlogPageType[] = [];

  // Don't want to fetch dynamic articles if curated
  if (isCurated) {
    return additionalArticles;
  }

  try {
    if (blogCategory) {
      loadedData = require(
        `../../../../public/blog/${toCamelCase(blogCategory)}.json`
      );
    } else {
      loadedData = require(`../../../../public/blog/allBlogs.json`);
    }

    additionalArticles = dedupe(loadedData, initialArticles);
  } catch {
    console.error('Could not find file for blog category or blog hub', {
      blogCategory,
    });
  }

  return additionalArticles;
}

const BlogArticle3Up = ({
  blogCategory,
  heading,
  initialArticles,
  isCurated = false,
}: {
  initialArticles: BlogPageType[];
  blogCategory?: string;
  heading?: string;
  isCurated?: boolean;
}) => {
  const [paginationIdx, setPaginationIdx] = useState(-1);
  const [mountedItemsIdx, setMountedItemsIdx] = useState(5);
  const { classes, cx } = useStyles();

  const additionalArticles = getAdditionalArticles(
    blogCategory,
    initialArticles,
    isCurated
  );

  const mountedItems = additionalArticles.slice(0, mountedItemsIdx + 1);
  const maxPaginationIdx = additionalArticles.length - 1;
  const showButton =
    initialArticles.length >= DYNAMIC_GRID_ARTICLE_LIMIT &&
    additionalArticles.length > 0 &&
    maxPaginationIdx - paginationIdx > 0;

  const clickHandler = () => {
    const futurePaginationIdx = Math.min(
      paginationIdx + DYNAMIC_GRID_ARTICLE_LIMIT,
      maxPaginationIdx
    );
    const futureMountedItemsIdx = Math.min(
      mountedItemsIdx + DYNAMIC_GRID_ARTICLE_LIMIT,
      maxPaginationIdx
    );

    setPaginationIdx(futurePaginationIdx);
    setMountedItemsIdx(futureMountedItemsIdx);
  };

  return (
    <SpacingContainer
      isFeatured
      className={classes.spacingContainer}
      data-testid='blogArticle3Up'
    >
      <GridContainer fixed>
        <Grid size={12}>
          {heading && <Heading level='h2'>{heading}</Heading>}
        </Grid>
      </GridContainer>
      <GridContainer fixed>
        {initialArticles.map(
          (
            {
              blogTitle,
              path,
              readTimeMinutes,
              category,
              categoryPath,
              heroImage,
            }: BlogPageType,
            idx: number
          ) => (
            <Grid
              key={`${blogTitle}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                idx
              }`}
              className={classes.cardWrapper}
              data-testid='blog-card-visible'
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              <BlogCard
                title={blogTitle}
                url={path}
                readTime={readTimeMinutes}
                category={category}
                image={heroImage}
                categoryPath={categoryPath}
              />
            </Grid>
          )
        )}
        {mountedItems.map(
          (
            {
              blogTitle,
              path,
              readTimeMinutes,
              category,
              categoryPath,
              heroImage,
            }: BlogPageType,
            idx: number
          ) => (
            <Grow
              in={idx <= paginationIdx}
              key={`${blogTitle}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                idx
              }`}
              timeout={
                BASE_DELAY +
                ((idx % DYNAMIC_GRID_ARTICLE_LIMIT) + 1) * DELAY_MULTIPLIER
              }
              data-testid={`blog-card-${
                idx <= paginationIdx ? 'visible' : 'hidden'
              }`}
              className={cx(classes.cardWrapper, {
                [classes.invisibleCard]: idx > paginationIdx,
              })}
            >
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <BlogCard
                  title={blogTitle}
                  url={path}
                  readTime={readTimeMinutes}
                  category={category}
                  image={heroImage}
                  categoryPath={categoryPath}
                />
              </Grid>
            </Grow>
          )
        )}
      </GridContainer>
      <GridContainer fixed>
        {showButton && (
          <Grid size={12}>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={clickHandler}
                label='Load more'
                variant={ButtonVariant.SECONDARY}
                iconName={IconName.CHEVRON_RIGHT}
                size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
                className={classes.buttonOverride}
              />
            </div>
          </Grid>
        )}
      </GridContainer>
    </SpacingContainer>
  );
};

export default BlogArticle3Up;
